import React, { useEffect, useState } from "react";
import { CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from "@/components/ui/command";
import { DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { typeMeta } from "@/lib/meta";
import { FolderKanban, Plus, LayoutDashboard, User as UserIcon, BarChart3 } from "lucide-react";

export default function CommandPalette({ open, setOpen, onOpenIssue, projects }) {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState({ issues: [], projects: [] });

  useEffect(() => {
    if (!open) { setQ(""); setResults({ issues: [], projects: [] }); return; }
  }, [open]);

  useEffect(() => {
    if (!q) { setResults({ issues: [], projects: [] }); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/search`, { params: { q } });
        setResults(r.data);
      } catch {}
    }, 150);
    return () => clearTimeout(t);
  }, [q]);

  const go = (path) => { setOpen(false); nav(path); };

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <VisuallyHidden><DialogTitle>Command Palette</DialogTitle><DialogDescription>Quickly search and navigate</DialogDescription></VisuallyHidden>
      <CommandInput value={q} onValueChange={setQ} placeholder="Search issues, projects, or actions…" data-testid="cmdk-input" />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>
        {(results.issues?.length > 0) && (
          <CommandGroup heading="Issues">
            {results.issues.map(i => {
              const T = typeMeta(i.issue_type);
              return (
                <CommandItem key={i.id} value={`${i.key} ${i.summary}`} onSelect={()=>{ setOpen(false); onOpenIssue(i.id); }} data-testid={`cmdk-issue-${i.key}`}>
                  <T.icon className={`w-4 h-4 mr-2 ${T.cls}`} />
                  <span className="font-mono text-xs mr-2 text-muted-foreground">{i.key}</span>
                  <span className="truncate">{i.summary}</span>
                </CommandItem>
              );
            })}
          </CommandGroup>
        )}
        {(results.projects?.length > 0) && (
          <CommandGroup heading="Projects">
            {results.projects.map(p => (
              <CommandItem key={p.id} value={p.name} onSelect={()=>go(`/projects/${p.id}`)}>
                <FolderKanban className="w-4 h-4 mr-2 text-primary" /> {p.name}
                <span className="ml-2 text-xs font-mono text-muted-foreground">{p.key}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        <CommandSeparator />
        <CommandGroup heading="Navigate">
          <CommandItem onSelect={()=>go("/")}><LayoutDashboard className="w-4 h-4 mr-2" /> Dashboard</CommandItem>
          <CommandItem onSelect={()=>go("/my-work")}><UserIcon className="w-4 h-4 mr-2" /> My Work</CommandItem>
          <CommandItem onSelect={()=>go("/projects")}><FolderKanban className="w-4 h-4 mr-2" /> Projects</CommandItem>
          <CommandItem onSelect={()=>go("/reports")}><BarChart3 className="w-4 h-4 mr-2" /> Reports</CommandItem>
        </CommandGroup>
        <CommandGroup heading="Quick projects">
          {projects.slice(0,6).map(p => (
            <CommandItem key={p.id} onSelect={()=>go(`/projects/${p.id}/board`)}>
              <span className="w-5 h-5 grid place-items-center rounded bg-primary/15 text-primary text-[10px] font-mono font-semibold mr-2">{p.key.slice(0,2)}</span>
              Open {p.name} board
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
